// packages/web-ui/src/components/slash-palette/commands.ts
//
// Story W2-S7 AC #7-#8 — single-source-of-truth slash command registry.
//
// Each command declares (id, label, category, icon, parameters, dispatch).
// Both `<SlashMenu>` (inline `Popover`) and `<GlobalPalette>` (modal `Dialog`)
// render from this same table — the only chrome difference between the two is
// the surrounding container.
//
// The static set covers built-in commands (`/new /clear /help /generate-view
// /health /regenerate /cancel`). Skills are surfaced as their own `/<skill-id>`
// entries built dynamically from the live skills list via `buildSkillCommands`
// (see `useSkillsForSlash`); the static table never lists individual skills.
// Templates are intentionally NOT exposed via slash — they remain accessible
// through the templates pane only.

import type { LucideIcon } from 'lucide-react';
import type { SkillSummary } from '@tessa-lean/base/api-schemas/index.js';
import {
  ChevronRight,
  HeartPulse,
  HelpCircle,
  Layers,
  Plus,
  RefreshCw,
  Square,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';

/** Categories used for grouping in the menu list. */
export type SlashCategory =
  | 'kb'
  | 'view'
  | 'navigation'
  | 'agent'
  | 'analysis'
  | 'system'
  | 'diff'
  | 'settings';

/** One parameter on a parameterised command (e.g. `/generate-view`). */
export interface SlashParameter {
  name: string;
  type: 'string' | 'enum';
  /** Required for `type: 'enum'`. */
  enumValues?: string[];
  placeholder?: string;
  /** Default value used when the user picks "Run with defaults" (Ctrl+Enter). */
  defaultValue?: string;
}

/**
 * Collaborator surface passed to every dispatch — keeps commands decoupled
 * from the SPA's panel imports. Dispatches stay pure (input → outbound API
 * call / navigation event); they never touch DOM state directly.
 */
export interface SlashDispatchContext {
  sessionId: string;
  /** Send a chat message with the given body (composer's serialised path). */
  sendMessage: (body: string) => Promise<void> | void;
  /** Create a new chat session and switch to it. Used by `/new`. */
  createSession?: () => Promise<void> | void;
  /** Clear messages in the active session. Used by `/clear`. */
  clearMessages?: () => Promise<void> | void;
  /** Open the keyboard cheat-sheet overlay. Used by `/help`. */
  openHelp?: () => void;
  /** Stop the active assistant turn. Used by `/cancel`. */
  cancelTurn?: () => void;
  /** Re-run the last user message. Used by `/regenerate`. */
  regenerateLast?: () => Promise<void> | void;
  /** Open a generic resource (entity / panel / overlay). Used by `/health`. */
  openResource?: (kind: string, payload?: Record<string, unknown>) => void;
  /** Toast emitter — used by stub commands to signal "not yet wired". */
  dispatchToast?: (message: string, severity?: 'info' | 'warning' | 'error') => void;
}

export interface SlashCommand {
  id: string;
  /** Human-readable label, e.g. `/generate-view <type> <subject>`. */
  label: string;
  /** Short description shown beside the label. */
  description: string;
  category: SlashCategory;
  icon: LucideIcon;
  parameters: SlashParameter[];
  /** When `true` the command is shown but stubbed — emits a toast on dispatch. */
  stub?: boolean;
  /** Story id reference for stubs (e.g. `W4-S5`). */
  stubStory?: string;
  dispatch: (
    params: Record<string, unknown>,
    ctx: SlashDispatchContext,
  ) => Promise<void> | void;
}

/** Build the orchestrator-facing body for `/generate-view`. */
function buildGenerateViewBody(params: Record<string, unknown>): string {
  const type = String(params.type ?? 'bdd');
  const subject = String(params.subject ?? '');
  return `Run generate_view tool with type=${type} subject=${subject}`;
}

/**
 * v1 command set. Key invariants:
 *   - `id` is unique, lowercase-kebab, used as both menu key and Tab-completion target.
 *   - Stub commands MUST set `stub: true` AND `stubStory` so the parent test surface
 *     can assert the toast wiring without hard-coding the message.
 *   - `dispatch` is async-friendly but most are synchronous; the menu host awaits.
 */
export const SLASH_COMMANDS: ReadonlyArray<SlashCommand> = Object.freeze([
  {
    id: 'new',
    label: '/new',
    description: 'Start a new chat session.',
    category: 'navigation',
    icon: Plus,
    parameters: [],
    dispatch: async (_params, ctx) => {
      if (ctx.createSession !== undefined) {
        await ctx.createSession();
      } else {
        ctx.dispatchToast?.('Create-session not wired yet — coming in W2-S8.', 'warning');
      }
    },
  },
  {
    id: 'clear',
    label: '/clear',
    description: 'Clear all messages in the current session.',
    category: 'navigation',
    icon: Trash2,
    parameters: [],
    dispatch: async (_params, ctx) => {
      if (ctx.clearMessages !== undefined) {
        await ctx.clearMessages();
      } else {
        ctx.dispatchToast?.('Clear-messages not wired yet.', 'warning');
      }
    },
  },
  {
    id: 'help',
    label: '/help',
    description: 'Show the keyboard cheat-sheet.',
    category: 'system',
    icon: HelpCircle,
    parameters: [],
    dispatch: (_params, ctx) => {
      if (ctx.openHelp !== undefined) {
        ctx.openHelp();
      } else {
        ctx.dispatchToast?.('Keyboard cheat-sheet not wired yet — coming in W2-S10.', 'warning');
      }
    },
  },
  {
    id: 'generate-view',
    label: '/generate-view <type> <subject>',
    description: 'Render a SysML diagram (BDD / IBD / STM / REQ).',
    category: 'view',
    icon: Layers,
    parameters: [
      {
        name: 'type',
        type: 'enum',
        enumValues: ['bdd', 'ibd', 'stm', 'req'],
        defaultValue: 'bdd',
      },
      {
        name: 'subject',
        type: 'string',
        placeholder: 'entity name (e.g. BatteryModule)',
      },
    ],
    dispatch: async (params, ctx) => {
      const subject = String(params.subject ?? '').trim();
      if (subject.length === 0) {
        ctx.dispatchToast?.('/generate-view requires a subject.', 'error');
        return;
      }
      await ctx.sendMessage(buildGenerateViewBody(params));
    },
  },
  {
    id: 'health',
    label: '/health',
    description: 'Open the health dashboard.',
    category: 'system',
    icon: HeartPulse,
    parameters: [],
    stub: true,
    stubStory: 'W4-S5',
    dispatch: (_params, ctx) => {
      // eslint-disable-next-line no-console
      console.warn('TODO(W4-S5): /health command not wired');
      if (ctx.openResource !== undefined) {
        ctx.openResource('health-dashboard');
      } else {
        ctx.dispatchToast?.('Health dashboard coming in W4-S5.', 'info');
      }
    },
  },
  {
    id: 'regenerate',
    label: '/regenerate',
    description: 'Re-run the last user message.',
    category: 'agent',
    icon: RefreshCw,
    parameters: [],
    dispatch: async (_params, ctx) => {
      if (ctx.regenerateLast !== undefined) {
        await ctx.regenerateLast();
      } else {
        ctx.dispatchToast?.('Regenerate not wired yet.', 'warning');
      }
    },
  },
  {
    id: 'cancel',
    label: '/cancel',
    description: 'Stop the active assistant turn.',
    category: 'agent',
    icon: Square,
    parameters: [],
    dispatch: (_params, ctx) => {
      if (ctx.cancelTurn !== undefined) {
        ctx.cancelTurn();
      } else {
        ctx.dispatchToast?.('Cancel not wired yet.', 'warning');
      }
    },
  },
]);

/** Filter the command list by substring match on the id. Case-insensitive. */
export function filterCommands(
  query: string,
  source: ReadonlyArray<SlashCommand> = SLASH_COMMANDS,
): SlashCommand[] {
  // Strip the leading `/`.
  const q = query.startsWith('/') ? query.slice(1) : query;
  if (q.length === 0) return [...source];
  const needle = q.toLowerCase();
  return source.filter((cmd) => cmd.id.toLowerCase().includes(needle));
}

/** Look up a command by id. */
export function findCommand(
  commandId: string,
  source: ReadonlyArray<SlashCommand> = SLASH_COMMANDS,
): SlashCommand | undefined {
  return source.find((c) => c.id === commandId);
}

/** Default-value extractor for parameter form. */
export function defaultParamsFor(command: SlashCommand): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const p of command.parameters) {
    if (p.defaultValue !== undefined) out[p.name] = p.defaultValue;
    else if (p.type === 'enum' && p.enumValues !== undefined && p.enumValues.length > 0)
      out[p.name] = p.enumValues[0];
    else out[p.name] = '';
  }
  return out;
}

/** Build the `/<id>(<param>=<value>, ...)` preview string used in the parameter form. */
export function buildPreview(command: SlashCommand, params: Record<string, unknown>): string {
  const fnName = command.id.replace(/-/g, '_');
  const args = command.parameters
    .map((p) => `${p.name}=${String(params[p.name] ?? '')}`)
    .join(', ');
  return `${fnName}(${args})`;
}

/**
 * Convert the live skills list into per-skill `/<skill-name>` slash commands.
 * Skills with `disable-user-invocation: true` are filtered out (per the
 * canonical SkillFrontmatter contract — those skills are model-only).
 * Each generated command takes no parameters and dispatches the same
 * "Activate skill: <name>" message the old static `/skill <name>` used.
 */
export function buildSkillCommands(
  skills: ReadonlyArray<SkillSummary>,
): SlashCommand[] {
  return skills
    .filter((s) => s['disable-user-invocation'] !== true)
    .map((s) => ({
      id: s.name,
      label: `/${s.name}`,
      description: s.description,
      category: 'agent' as const,
      icon: Wand2,
      parameters: [],
      dispatch: async (_params, ctx) => {
        await ctx.sendMessage(`Activate skill: ${s.name}`);
      },
    }));
}

/** Test-only icon reference exporter (avoids dup-imports in tests). */
export const _ICONS = {
  Plus,
  Trash2,
  HelpCircle,
  Wand2,
  Layers,
  HeartPulse,
  RefreshCw,
  Square,
  X,
  ChevronRight,
};
